/**
 * Platform Admin Service
 * Handles authentication and authorization for platform-level administration
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export interface PlatformAdmin {
  id: string;
  clerk_user_id: string;
  email?: string;
  name?: string;
  permissions: {
    manage_categories?: boolean;
    manage_products?: boolean;
    manage_system?: boolean;
  };
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
  notes?: string;
}

class PlatformAdminService {
  /**
   * Check if the current user is a platform admin
   */
  async isPlatformAdmin(clerkUserId: string): Promise<boolean> {
    try {
      logger.info('Checking platform admin for Clerk user:', clerkUserId);
      const supabase = await getAuthenticatedSupabaseClient();

      const { data, error } = await supabase
        .from('platform_admins')
        .select('id, is_active')
        .eq('clerk_user_id', clerkUserId)
        .eq('is_active', true)
        .maybeSingle();

      logger.info('Platform admin query result:', { clerkUserId, data, error });

      if (error || !data) {
        logger.warn('User is not a platform admin:', { clerkUserId, error });
        return false;
      }

      // Update last login
      await this.updateLastLogin(clerkUserId);

      return true;
    } catch (error) {
      logger.error('Error checking platform admin status:', error);
      return false;
    }
  }

  /**
   * Get platform admin details
   */
  async getAdminDetails(clerkUserId: string): Promise<PlatformAdmin | null> {
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('platform_admins')
        .select('*')
        .eq('clerk_user_id', clerkUserId)
        .maybeSingle();

      if (error || !data) {
        return null;
      }

      return data as PlatformAdmin;
    } catch (error) {
      logger.error('Error fetching admin details:', error);
      return null;
    }
  }

  /**
   * Update last login timestamp
   */
  private async updateLastLogin(clerkUserId: string): Promise<void> {
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      await supabase
        .from('platform_admins')
        .update({ last_login_at: new Date().toISOString() })
        .eq('clerk_user_id', clerkUserId);
    } catch (error) {
      logger.error('Error updating last login:', error);
    }
  }

  /**
   * List all platform admins (only accessible by platform admins)
   */
  async listAdmins(): Promise<PlatformAdmin[]> {
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('platform_admins')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        logger.error('Error listing platform admins:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error('Error listing platform admins:', error);
      return [];
    }
  }

  /**
   * Add a new platform admin (only accessible by platform admins)
   */
  async addAdmin(
    clerkUserId: string,
    email?: string,
    name?: string,
    notes?: string,
  ): Promise<PlatformAdmin | null> {
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('platform_admins')
        .insert({
          clerk_user_id: clerkUserId,
          email,
          name,
          notes,
        })
        .select()
        .single();

      if (error) {
        logger.error('Error adding platform admin:', error);
        return null;
      }

      return data as PlatformAdmin;
    } catch (error) {
      logger.error('Error adding platform admin:', error);
      return null;
    }
  }

  /**
   * Update admin permissions
   */
  async updatePermissions(
    adminId: string,
    permissions: Partial<PlatformAdmin['permissions']>,
  ): Promise<boolean> {
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { error } = await supabase
        .from('platform_admins')
        .update({ permissions })
        .eq('id', adminId);

      if (error) {
        logger.error('Error updating permissions:', error);
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Error updating permissions:', error);
      return false;
    }
  }

  /**
   * Deactivate a platform admin
   */
  async deactivateAdmin(adminId: string): Promise<boolean> {
    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { error } = await supabase
        .from('platform_admins')
        .update({ is_active: false })
        .eq('id', adminId);

      if (error) {
        logger.error('Error deactivating admin:', error);
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Error deactivating admin:', error);
      return false;
    }
  }

  /**
   * Check specific permission for a user
   */
  async hasPermission(
    clerkUserId: string,
    permission: keyof PlatformAdmin['permissions'],
  ): Promise<boolean> {
    const admin = await this.getAdminDetails(clerkUserId);
    if (!admin || !admin.is_active) {
      return false;
    }

    return admin.permissions[permission] === true;
  }
}

export const platformAdminService = new PlatformAdminService();
